// src/components/landing/DesempenhoBlock.tsx
// Bloco 06 — Desempenho & Comunidade | GRAIA Landing
// Prefixo CSS: dsmb-

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, ArrowUpRight } from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer,
} from 'recharts';
import './DesempenhoBlock.css';

// ─────────────────────────────────────────────
// DADOS
// ─────────────────────────────────────────────
const WEEKLY_DATA = [42, 58, 51, 74, 67, 83, 91];
const DAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const DIFFICULTY_DATA: Record<string, { easy: number; medium: number; hard: number }> = {
    '7d': { easy: 92, medium: 78, hard: 61 },
    '30d': { easy: 88, medium: 74, hard: 57 },
    '90d': { easy: 85, medium: 71, hard: 54 },
};

const CATEGORIES = [
    { label: 'Fisiologia', value: 84 },
    { label: 'Bioquímica', value: 72 },
    { label: 'Neurologia', value: 61 },
];

const GROUP_RANKING = [
    { pos: 1, name: 'Rafael G.', pts: 1240, delta: '+48' },
    { pos: 2, name: 'Amanda', pts: 1112, delta: '+31' },
    { pos: 3, name: 'Bianca V.', pts: 1034, delta: '+22' },
    { pos: 4, name: 'Você', pts: 978, delta: '+19', isUser: true },
    { pos: 5, name: 'João', pts: 891, delta: '+14' },
];

const GLOBAL_RANKING = [
    { pos: 1, name: 'Neuro 2026', score: 1210 },
    { pos: 2, name: 'Med UF', score: 1143 },
    { pos: 3, name: 'Liga de Pediatria', score: 1089 },
    { pos: 4, name: 'Seu grupo', score: 978, isUser: true },
];

const CHART_DATA = WEEKLY_DATA.map((v, i) => ({ day: DAYS[i], value: v }));

// ─────────────────────────────────────────────
// GRÁFICO
// ─────────────────────────────────────────────
const ChartTooltip: React.FC<any> = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="dsmb-tooltip">
            <span className="dsmb-tooltip-val">{payload[0].value}</span>
            <span className="dsmb-tooltip-label">/dia</span>
        </div>
    );
};

const ChartDot: React.FC<any> = ({ cx, cy }) =>
    <circle cx={cx} cy={cy} r={4} fill="#fff" stroke="#2F7130" strokeWidth={2} />;

const LineChart: React.FC<{ animated: boolean }> = ({ animated }) => {
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        if (!animated) { setIsActive(false); return; }
        // Gráfico começa a desenhar após os contadores (800ms)
        const t = setTimeout(() => setIsActive(true), 800);
        return () => clearTimeout(t);
    }, [animated]);

    return (
        <div className="dsmb-chart-container">
            <ResponsiveContainer width="100%" height={100}>
                <AreaChart data={CHART_DATA} margin={{ top: 14, right: 4, left: 4, bottom: 0 }}>
                    <defs>
                        <linearGradient id="dsmb-grad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2F7130" stopOpacity={0.18} />
                            <stop offset="100%" stopColor="#2F7130" stopOpacity={0.01} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid horizontal vertical={false} stroke="#E5E7EB" strokeDasharray="4 4" />
                    <XAxis
                        dataKey="day"
                        tick={{ fontSize: 9, fill: '#D1D5DB', fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
                        tickLine={false} axisLine={false} interval={0} dy={4}
                    />
                    <YAxis hide />
                    <Tooltip
                        content={<ChartTooltip />}
                        cursor={{ stroke: '#2F7130', strokeWidth: 1, strokeDasharray: '4 4' }}
                    />
                    <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#2F7130"
                        strokeWidth={2}
                        fill="url(#dsmb-grad)"
                        dot={<ChartDot />}
                        activeDot={{ r: 5, fill: '#2F7130', stroke: '#fff', strokeWidth: 2 }}
                        isAnimationActive={isActive}
                        animationDuration={2400}   // lento — era 1400
                        animationEasing="ease-out"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

// ─────────────────────────────────────────────
// CONTADOR ANIMADO — mais lento, overshoot suave
// ─────────────────────────────────────────────
const AnimatedCounter: React.FC<{
    target: number;
    duration?: number;
    active: boolean;
    suffix?: string;
    delay?: number;
}> = ({ target, duration = 1800, active, suffix = '', delay = 0 }) => {
    const [value, setValue] = useState(0);
    const startRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        if (!active) { setValue(0); return; }
        const timer = setTimeout(() => {
            const ease = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            const animate = (ts: number) => {
                if (!startRef.current) startRef.current = ts;
                const progress = Math.min((ts - startRef.current) / duration, 1);
                // overshoot suave: sobe 4% acima do target e pousa
                const overshoot = progress < 0.85
                    ? ease(progress / 0.85) * (target * 1.04)
                    : target * 1.04 - (ease((progress - 0.85) / 0.15) * target * 0.04);
                setValue(Math.round(overshoot));
                if (progress < 1) rafRef.current = requestAnimationFrame(animate);
                else setValue(target);
            };
            rafRef.current = requestAnimationFrame(animate);
        }, delay);
        return () => {
            clearTimeout(timer);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            startRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, target, duration, delay]);

    return <>{value.toLocaleString('pt-BR')}{suffix}</>;
};

// ─────────────────────────────────────────────
// COLUNAS VERTICAIS — bem mais devagar
// ─────────────────────────────────────────────
const VerticalBars: React.FC<{
    data: { label: string; val: number; color: string }[];
    active: boolean;
}> = ({ data, active }) => {
    const [heights, setHeights] = useState(data.map(() => 0));

    useEffect(() => {
        if (!active) { setHeights(data.map(() => 0)); return; }
        data.forEach((bar, i) => {
            // delay maior entre cada barra: 800ms base + 280ms entre elas
            setTimeout(() => {
                setHeights(prev => {
                    const next = [...prev]; next[i] = bar.val; return next;
                });
            }, 800 + i * 280);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return (
        <div className="dsmb-vbars">
            {data.map((bar, i) => (
                <div key={bar.label} className="dsmb-vbar-col">
                    <span className="dsmb-vbar-val" style={{ color: bar.color }}>{heights[i]}%</span>
                    <div className="dsmb-vbar-track">
                        <div
                            className="dsmb-vbar-fill"
                            style={{
                                height: `${heights[i]}%`,
                                background: bar.color,
                                transition: heights[i] === 0
                                    ? 'none'
                                    : 'height 1.4s cubic-bezier(0.34, 1.15, 0.64, 1)', // era 0.7s
                            }}
                        />
                    </div>
                    <span className="dsmb-vbar-label">{bar.label}</span>
                </div>
            ))}
        </div>
    );
};

// ─────────────────────────────────────────────
// PAINEL ESQUERDO — MÉTRICAS
// ─────────────────────────────────────────────
const MetricsPanel: React.FC<{ active: boolean }> = ({ active }) => {
    const [activePeriod] = useState<'7d' | '30d' | '90d'>('7d');
    const diffData = DIFFICULTY_DATA[activePeriod];

    const vbarData = [
        { label: 'Fácil', val: diffData.easy, color: '#16a34a' },
        { label: 'Médio', val: diffData.medium, color: '#d97706' },
        { label: 'Difícil', val: diffData.hard, color: '#dc2626' },
    ];

    return (
        <div className="dsmb-panel">
            <div className="dsmb-panel-header">
                <span className="dsmb-panel-logo">GRAIA</span>
                <span className="dsmb-panel-sep" />
                <span className="dsmb-panel-title">Meu Desempenho</span>
            </div>

            <div className="dsmb-panel-body">
                {/* Resumo — contadores com delays escalonados */}
                <div className="dsmb-summary-row">
                    <div className="dsmb-summary-stat">
                        <span className="dsmb-summary-val">
                            {active
                                ? <AnimatedCounter target={347} active={active} duration={1800} delay={200} />
                                : '0'}
                        </span>
                        <span className="dsmb-summary-label">cards dominados</span>
                    </div>
                    <div className="dsmb-summary-stat">
                        <span className="dsmb-summary-val">
                            {active
                                ? <AnimatedCounter target={81} active={active} duration={1600} delay={450} suffix="%" />
                                : '0%'}
                        </span>
                        <span className="dsmb-summary-label">domínio geral</span>
                    </div>
                    <div className="dsmb-summary-badge">
                        <ArrowUpRight size={11} />
                        +12% esta semana
                    </div>
                </div>

                {/* Gráfico */}
                <div className="dsmb-section-block">
                    <div className="dsmb-block-header">
                        <span className="dsmb-block-label">Evolução semanal</span>
                        <span className="dsmb-block-unit">cards/dia</span>
                    </div>
                    <LineChart animated={active} />
                </div>

                {/* Dificuldade */}
                <div className="dsmb-section-block">
                    <div className="dsmb-block-header">
                        <span className="dsmb-block-label">Por dificuldade</span>
                        <div className="dsmb-period-tabs">
                            {(['7d', '30d', '90d'] as const).map(p => (
                                <span key={p}
                                    className={`dsmb-period-tab ${activePeriod === p ? 'dsmb-period-tab--active' : ''}`}>
                                    {p}
                                </span>
                            ))}
                        </div>
                    </div>
                    <VerticalBars data={vbarData} active={active} />
                </div>

                {/* Categorias — delays muito maiores */}
                <div className="dsmb-section-block dsmb-section-block--last">
                    <span className="dsmb-block-label">Por disciplina</span>
                    <div className="dsmb-cats">
                        {CATEGORIES.map((cat, i) => (
                            <div key={cat.label} className="dsmb-cat-row">
                                <span className="dsmb-cat-label">{cat.label}</span>
                                <div className="dsmb-cat-track">
                                    <div
                                        className="dsmb-cat-fill"
                                        style={{
                                            width: active ? `${cat.value}%` : '0%',
                                            transitionDelay: active ? `${1600 + i * 220}ms` : '0ms',
                                        }}
                                    />
                                </div>
                                <span className="dsmb-cat-val">{cat.value}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// PAINEL DIREITO — GRUPOS
// ─────────────────────────────────────────────
const GroupsPanel: React.FC<{ active: boolean }> = ({ active }) => {
    const [view, setView] = useState<'group' | 'global'>('group');
    const [rowsVisible, setRowsVisible] = useState(false);
    const [badgeVisible, setBadgeVisible] = useState(false);

    useEffect(() => {
        if (!active) { setRowsVisible(false); setBadgeVisible(false); return; }
        // rows: aparecem devagar após a seção estar visível
        const t1 = setTimeout(() => setRowsVisible(true), 700);   // era 300
        // badge: aparece bem depois, como revelação final
        const t2 = setTimeout(() => setBadgeVisible(true), 2000); // era 900
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [active]);

    useEffect(() => {
        if (!active) return;
        // Troca para global bem mais devagar — dá tempo do usuário ler
        const t = setTimeout(() => setView('global'), 7000); // era 4800
        return () => clearTimeout(t);
    }, [active]);

    return (
        <div className="dsmb-panel">
            <div className="dsmb-panel-header">
                <span className="dsmb-panel-logo">GRAIA</span>
                <span className="dsmb-panel-sep" />
                <span className="dsmb-panel-title">Grupos</span>
            </div>

            <div className="dsmb-panel-body">
                <div className="dsmb-group-card">
                    <div className="dsmb-group-avatar">MED</div>
                    <div className="dsmb-group-info">
                        <span className="dsmb-group-name">Med 2025 · Turma B</span>
                        <div className="dsmb-group-meta-row">
                            <span className="dsmb-group-meta">12 membros</span>
                            <span className="dsmb-group-meta-sep">·</span>
                            <span className="dsmb-group-meta">Média: 842 pts</span>
                        </div>
                    </div>
                </div>

                <div className="dsmb-group-stats">
                    <div className="dsmb-group-stat">
                        <span className="dsmb-group-stat-val">
                            {active
                                ? <AnimatedCounter target={12} active={active} duration={1200} delay={400} />
                                : '0'}
                        </span>
                        <span className="dsmb-group-stat-label">membros ativos</span>
                    </div>
                    <div className="dsmb-group-stat-div" />
                    <div className="dsmb-group-stat">
                        <span className="dsmb-group-stat-val">
                            {active
                                ? <AnimatedCounter target={842} active={active} duration={2000} delay={700} />
                                : '0'}
                        </span>
                        <span className="dsmb-group-stat-label">score médio</span>
                    </div>
                    <div className="dsmb-group-stat-div" />
                    <div className="dsmb-group-stat">
                        {/* Badge aparece como revelação — delay longo */}
                        <span className={`dsmb-global-badge ${badgeVisible ? 'dsmb-global-badge--visible' : ''}`}>
                            #4
                        </span>
                        <span className="dsmb-group-stat-label">ranking global</span>
                    </div>
                </div>

                <div className="dsmb-rank-tabs">
                    {(['group', 'global'] as const).map(v => (
                        <button
                            key={v}
                            className={`dsmb-rank-tab ${view === v ? 'dsmb-rank-tab--active' : ''}`}
                            onClick={() => setView(v)}
                        >
                            {v === 'group' ? 'Grupo' : 'Global'}
                        </button>
                    ))}
                </div>

                {view === 'group' && (
                    <div className="dsmb-ranking-list">
                        {GROUP_RANKING.map((row, i) => (
                            <div
                                key={row.name}
                                className={[
                                    'dsmb-rank-row',
                                    row.isUser ? 'dsmb-rank-row--user' : '',
                                    rowsVisible ? 'dsmb-rank-row--visible' : '',
                                ].join(' ')}
                                style={{ transitionDelay: `${i * 120}ms` }} // era 70ms
                            >
                                <span className={`dsmb-rank-pos ${row.pos <= 3 ? `dsmb-rank-pos--${row.pos}` : ''}`}>
                                    {row.pos}
                                </span>
                                <span className="dsmb-rank-name">{row.name}</span>
                                <span className="dsmb-rank-delta">{row.delta}</span>
                                <span className="dsmb-rank-pts">{row.pts.toLocaleString('pt-BR')}</span>
                            </div>
                        ))}
                    </div>
                )}

                {view === 'global' && (
                    <div className="dsmb-ranking-list">
                        {GLOBAL_RANKING.map((row, i) => (
                            <div
                                key={row.name}
                                className={[
                                    'dsmb-rank-row',
                                    row.isUser ? 'dsmb-rank-row--user' : '',
                                    'dsmb-rank-row--visible',
                                ].join(' ')}
                                style={{ transitionDelay: `${i * 90}ms` }}
                            >
                                <span className={`dsmb-rank-pos ${row.pos <= 3 ? `dsmb-rank-pos--${row.pos}` : ''}`}>
                                    {row.pos}
                                </span>
                                <span className="dsmb-rank-name">{row.name}</span>
                                <span className="dsmb-rank-pts">{row.score.toLocaleString('pt-BR')}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const DesempenhoBlock: React.FC = () => {
    const navigate = useNavigate();
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
            { threshold: 0.12 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section
            className={`dsmb-section ${visible ? 'dsmb-section--visible' : ''}`}
            ref={sectionRef}
        >
            <div className="dsmb-container">

                <div className="dsmb-header">
                    <p className="dsmb-label">Desempenho &amp; Comunidade</p>
                    <h2 className="dsmb-headline">
                        Estude com dados.<br />Evolua com pessoas.
                    </h2>
                    <p className="dsmb-subheadline">
                        Cada sessão é registrada. Cada progresso é visível.<br />
                        E cada esforço pode impulsionar seu grupo.
                    </p>
                </div>

                <div className="dsmb-split-grid">

                    <div className="dsmb-split-col">
                        <div className="dsmb-split-eyebrow">
                            <TrendingUp size={14} />Individual
                        </div>
                        <MetricsPanel active={visible} />
                        <p className="dsmb-split-copy">
                            <strong>Você não estuda no escuro.</strong> Cada número é recalculado em tempo real — cards por dia, taxa de acerto, evolução e desempenho por disciplina.
                        </p>
                    </div>

                    <div className="dsmb-divider" aria-hidden="true">
                        <div className="dsmb-divider-line" />
                        <div className="dsmb-divider-dot" />
                        <div className="dsmb-divider-line" />
                    </div>

                    <div className="dsmb-split-col">
                        <div className="dsmb-split-eyebrow">
                            <Users size={14} />Coletivo
                        </div>
                        <GroupsPanel active={visible} />
                        <p className="dsmb-split-copy">
                            O mesmo motor que calcula sua evolução individual alimenta a pontuação do seu grupo.{' '}
                            <strong>É desempenho real transformado em placar.</strong>
                        </p>
                    </div>

                </div>

                <div className="dsmb-closing">
                    <p className="dsmb-closing-main">
                        Você pode estudar sozinho.<br />
                        <strong>Mas não precisa evoluir sozinho.</strong>
                    </p>
                    <button className="dsmb-cta" onClick={() => navigate('/cadastro')}>
                        Criar conta gratuitamente
                    </button>
                </div>

            </div>
        </section>
    );
};

export default DesempenhoBlock;